/**
 * =============================================================================
 * Hook: useReferralCommissionDetails
 * Versão: 1.0.0
 *
 * Descrição:
 *   Carrega TODOS os detalhes de uma comissão do módulo Indicação em uma única
 *   chamada, via RPC `get_referral_commission_details`. Espelha o padrão de
 *   `useReferralPayoutDetails` (consistência arquitetural):
 *
 *   - 100% server-side: nada de SELECT direto na tabela.
 *   - Realtime: invalida o cache em qualquer UPDATE em `affiliate_commissions`
 *     filtrado pelo id, e em INSERT/DELETE em `affiliate_payout_commission_links`
 *     com `commission_id` igual (timeline e bloco de pagamentos vinculados).
 *   - Habilita-se apenas quando há `commissionId` E o Sheet está aberto.
 *
 * IMPORTANTE — Mascaramento de UUIDs:
 *   A RPC entrega o JSON já passado por `format_jsonb_uuids`, então todos os
 *   ids vêm em 8 chars uppercase. Para manter compatibilidade com componentes
 *   que esperam strings, tipamos como `string` mesmo assim. O frontend NÃO
 *   deve depender do UUID completo a partir desta resposta.
 *
 * Release notes:
 *   v1.0.0 - Criação para alimentar o `CommissionDetailSheet`.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { CommissionStatus, RewardType } from '@/hooks/useReferralCommissionsPaginated';

const logger = createLogger('useReferralCommissionDetails');

/** Bloco principal "commission" com joins de origem (deal/cliente/vendedor). */
export interface CommissionDetailsCommission {
  id: string;
  tenant_id: string;
  status: CommissionStatus;
  reward_type: RewardType;
  reward_value: number | null;
  base_value: number | null;
  currency: string | null;
  points_earned: number | null;
  calculation_details: Record<string, unknown> | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  approved_at: string | null;
  approved_by: string | null;
  paid_at: string | null;
  payout_id: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  created_at: string;
  updated_at: string;
  affiliate_id: string | null;
  referral_id: string | null;
  program_id: string | null;
  campaign_id: string | null;
  membership_id: string | null;
  // Joins
  program_name: string | null;
  campaign_name: string | null;
  deal_id: string | null;
  deal_title: string | null;
  deal_value: number | null;
  deal_phase_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_cpf: string | null;
  customer_cnpj: string | null;
  seller_id: string | null;
  seller_name: string | null;
  seller_email: string | null;
  seller_avatar_url: string | null;
  referral_lead_name: string | null;
  referral_lead_email: string | null;
  referral_lead_phone: string | null;
  approved_by_name: string | null;
  approved_by_email: string | null;
  // v1.1.0 — campos extras do negócio (paridade visual com Sheet de Indicação)
  deal_status: 'open' | 'won' | 'lost' | null;
  deal_phase_color: string | null;
  deal_phase_sort_order: number | null;
  deal_pipeline_id: string | null;
  deal_pipeline_total_phases: number | null;
  deal_workspace_name: string | null;
  deal_category_name: string | null;
  deal_source: string | null;
}

/** Dados resumidos do afiliado dono da comissão. */
export interface CommissionDetailsAffiliate {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  phone: string | null;
  pix_key: string | null;
  pix_key_type: string | null;
}

/** Pagamento (payout) vinculado à comissão (N:N via `affiliate_payout_commission_links`). */
export interface CommissionDetailsPayout {
  link_id: string;
  payout_id: string;
  payout_status: string;
  payout_amount: number | null;
  payout_currency: string | null;
  payout_method: string | null;
  external_transaction_id: string | null;
  requested_at: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  link_reward_value: number | null;
  linked_at: string | null;
  released_at: string | null;
  released_reason: string | null;
  is_active: boolean;
}

/** Tipos de evento sintéticos da timeline. */
export type CommissionTimelineAction =
  | 'created'
  | 'approved'
  | 'payout_linked'
  | 'payout_released'
  | 'paid'
  | 'cancelled';

/** Evento individual da timeline cronológica. */
export interface CommissionTimelineEvent {
  id: string;
  action: CommissionTimelineAction;
  reference_id: string | null;
  reference_type: string | null;
  performer_name: string | null;
  performer_email: string | null;
  reason: string | null;
  notes: string | null;
  created_at: string;
}

/** Estrutura completa retornada pela RPC `get_referral_commission_details`. */
export interface CommissionDetailsResult {
  commission: CommissionDetailsCommission;
  affiliate: CommissionDetailsAffiliate | null;
  payouts: CommissionDetailsPayout[];
  timeline: CommissionTimelineEvent[];
}

/**
 * Hook principal — busca detalhes da comissão + assina realtime.
 *
 * @param commissionId  UUID da comissão (`null`/`undefined` desabilita).
 * @param open          Se o Sheet está aberto (controla enabled e subscription).
 */
export function useReferralCommissionDetails(
  commissionId: string | undefined | null,
  open: boolean,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // ── Realtime ──
  // Invalida o cache em qualquer mudança que afete os blocos exibidos.
  useEffect(() => {
    if (!commissionId || !open) return;

    const channel = supabase
      .channel(`commission-rt-${commissionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `id=eq.${commissionId}`,
        },
        () => {
          logger.debug('Comissão alterada via realtime, invalidando cache', { commissionId });
          queryClient.invalidateQueries({
            queryKey: ['referral-commission-details', commissionId],
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_payout_commission_links',
          filter: `commission_id=eq.${commissionId}`,
        },
        () => {
          logger.debug('Vínculo de pagamento alterado via realtime', { commissionId });
          queryClient.invalidateQueries({
            queryKey: ['referral-commission-details', commissionId],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [commissionId, open, queryClient]);

  return useQuery({
    queryKey: ['referral-commission-details', commissionId],
    enabled: !!commissionId && open,
    queryFn: async (): Promise<CommissionDetailsResult> => {
      if (!commissionId) throw new Error('commissionId é obrigatório');

      // Casting até o `types.ts` ser regenerado com a nova RPC.
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_commission_details',
        {
          p_commission_id: commissionId,
          p_tenant_hint: currentTenant?.id ?? null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar detalhes da comissão via RPC', error);
        throw error;
      }

      return data as CommissionDetailsResult;
    },
    staleTime: 30 * 1000,
  });
}
